// Справка „Взаимозаменени продукти" — замените от ВСИЧКИ складове за периода:
// хедър „Справка" с подзаглавие, период (7 пресета + календар, по
// подразбиране „Този месец") и Excel; KPI за периода, търсачка, филтри В
// ЗАГЛАВИЯТА НА КОЛОНИТЕ (Тип · Склад · Потребител · Донор · Получател) с
// фасетни броячи и ред „Филтри: …" над таблицата, колони, които сортират,
// „Покажи още" и ред „Общо за периода" (карти на телефон).
import { useCallback, useMemo, useState, type FC } from "react";
import { EuroOutlined, SwapOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import { useSubstitutions, useProducts, useInventorySettings } from "../../../hooks/useInventory";
import { exportToExcel } from "../../../utils/excelExport";
import { InventoryTheme } from "../components/InventoryTheme";
import { PageHeader } from "../components/list/PageHeader";
import { EmptyScreen, KpiGrid, SearchBox, SectionCard, type KpiCard } from "../components/list/ListBits";
import { ReportToolbar } from "../components/list/ReportToolbar";
import { refreshOf } from "../components/list/reportRefresh";
import { formatPeriod, periodOfPreset, periodToParams, type Period } from "../components/period/periodPresets";
import { FilterSummaryLine } from "../components/filters/FilterSummaryLine";
import { allSections, useColumnFilters, type ColumnSections } from "../components/filters/useColumnFilters";
import { useShowMore } from "../components/list/useShowMore";
import { fmtDateTime } from "../components/dateTime";
import { formatMoney } from "../components/numbers";
import { nextSort, type LegacyReportProps } from "./components/reportListModel";
import { ReportLoading, ReportNoMatch, ResultSum, ShowMoreRow } from "./components/ReportListBits";
import {
  EMPTY_SUB_FILTERS,
  buildSubstitutionRows,
  searchSubstitutions,
  sortSubstitutions,
  subSortFirstDir,
  substitutionFilterSpecs,
  substitutionTotals,
  type SubFilterKey,
  type SubSort,
  type SubSortKey,
} from "./components/substitutionsReportModel";
import { SubstitutionsReportTable, type SubstitutionsDensity } from "./components/SubstitutionsReportTable";

export const SubstitutionsReport: FC<LegacyReportProps> = () => {
  const { t } = useTranslation();
  const { isNarrow, isWide } = useViewport();

  const [period, setPeriod] = useState<Period>(() => periodOfPreset("thisMonth"));
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SubSort | null>(null);

  // Всички замени от ВСИЧКИ складове (не зависи от избрания склад).
  const movementsQuery = useSubstitutions(periodToParams(period));
  const { data: movements = [], isLoading, isFetching } = movementsQuery;
  const productsQuery = useProducts();
  const { data: products = [] } = productsQuery;
  const { refresh, refreshing } = refreshOf(movementsQuery, productsQuery);
  const { data: settings } = useInventorySettings();
  // Себестойността (на донора) е винаги нето; бруто по регистрацията по ДДС.
  const vatRegistered = settings?.vatRegistered ?? true;

  const sourceLabel = useCallback(
    (source: string) => (source === "stocktake" ? t("inventoryPage.subSourceStocktake") : source),
    [t],
  );

  const allRows = useMemo(
    () => buildSubstitutionRows(movements, products, vatRegistered),
    [movements, products, vatRegistered],
  );
  // ─── Филтри в колоните (броячите — фасетни, по периода след търсенето) ──
  const specs = useMemo(
    () =>
      substitutionFilterSpecs({
        titles: {
          types: t("inventoryPage.colType"),
          warehouses: t("inventoryPage.colWarehouse"),
          users: t("inventoryPage.colUser"),
          donors: t("inventoryPage.subColFrom"),
          recipients: t("inventoryPage.subColTo"),
        },
        sourceLabel,
      }),
    [t, sourceLabel],
  );
  const searched = useMemo(
    () => searchSubstitutions(allRows, query, sourceLabel),
    [allRows, query, sourceLabel],
  );
  const filters = useColumnFilters({ specs, empty: EMPTY_SUB_FILTERS, universe: allRows, base: searched });
  const shown = useMemo(() => sortSubstitutions(filters.rows, sort), [filters.rows, sort]);
  // Низ, не масив — useShowMore нулира при СМЯНА на ключа
  const resetKey = JSON.stringify([formatPeriod(period), filters.state, query, sort]);
  const { visible, hiddenCount, showMore } = useShowMore(shown, resetKey);
  // KPI-тата са за периода; редът над таблицата — за намереното
  const kpis = substitutionTotals(allRows);
  const totals = substitutionTotals(shown);
  const clearAll = () => {
    filters.clearAll();
    setQuery("");
  };
  const sections: ColumnSections<SubFilterKey> = {
    types: filters.section("types"),
    warehouses: filters.section("warehouses"),
    users: filters.section("users"),
    donors: filters.section("donors"),
    recipients: filters.section("recipients"),
  };

  const kpiCards: KpiCard[] = [
    { label: t("inventoryPage.colSubCount"), value: String(kpis.count), Icon: SwapOutlined },
    { label: t("inventoryPage.kpiValueGross"), value: formatMoney(kpis.gross), Icon: EuroOutlined, tone: "brand" },
  ];

  const exportXls = () =>
    exportToExcel({
      filename: "vzaimozameneni",
      sheetName: t("inventoryPage.substitutionsReport"),
      rows: shown,
      columns: [
        { header: t("inventoryPage.colDate"), value: (row) => fmtDateTime(row.date) },
        { header: t("inventoryPage.colUser"), value: (row) => row.createdByName ?? "" },
        { header: t("inventoryPage.colType"), value: (row) => sourceLabel(row.source) },
        { header: t("inventoryPage.colWarehouse"), value: (row) => row.warehouseName ?? "" },
        { header: t("inventoryPage.subColFrom"), value: (row) => row.donorName },
        { header: t("inventoryPage.subWrittenOff"), value: (row) => row.donorQty },
        { header: t("inventoryPage.colUnit"), value: (row) => row.donorUnit ?? "" },
        { header: t("inventoryPage.subColTo"), value: (row) => row.recipientName },
        { header: t("inventoryPage.subCovered"), value: (row) => row.recipientQty },
        { header: t("inventoryPage.colUnit"), value: (row) => row.recipientUnit ?? "" },
        { header: t("inventoryPage.colUnitPrice"), value: (row) => row.unitPrice },
        { header: t("inventoryPage.colNet"), value: (row) => Number(row.sumNoVat.toFixed(2)) },
        { header: t("inventoryPage.colGross"), value: (row) => Number(row.sum.toFixed(2)) },
      ],
    });

  const density: SubstitutionsDensity = isNarrow ? "cards" : isWide ? "full" : "medium";
  const onSort = (key: SubSortKey) => setSort((current) => nextSort(current, key, subSortFirstDir(key)));

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          kicker={t("inventoryPage.kickerReport")}
          title={t("inventoryPage.substitutionsReport")}
          subtitle={isLoading ? undefined : t("inventoryPage.subSubtitle", { count: kpis.count })}
          right={
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              filterSections={allSections(sections)}
              onExport={exportXls}
              exportDisabled={!shown.length}
              onRefresh={refresh}
              refreshing={refreshing}
            />
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <KpiGrid cards={kpiCards} />
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={query} onChange={setQuery} placeholder={t("inventoryPage.subSearchPh")} />
          </div>
          <SectionCard className={isFetching && !isLoading ? "opacity-60" : ""}>
            <FilterSummaryLine
              countLabel={t("inventoryPage.reportResult", { count: shown.length })}
              items={filters.summary}
              onClearAll={filters.clearAll}
            >
              <ResultSum label={t("inventoryPage.colGross")} value={formatMoney(totals.gross)} strong />
            </FilterSummaryLine>
            {isLoading ? (
              <ReportLoading />
            ) : allRows.length === 0 ? (
              <EmptyScreen Icon={SwapOutlined} tone="brand" title={t("inventoryPage.noSubstitutions")} />
            ) : (
              <SubstitutionsReportTable
                rows={visible}
                density={density}
                sourceLabel={sourceLabel}
                sort={sort}
                onSort={onSort}
                filters={sections}
                totals={totals}
                footer={
                  shown.length ? (
                    <ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />
                  ) : (
                    <ReportNoMatch onClear={clearAll} />
                  )
                }
              />
            )}
          </SectionCard>
        </div>
      </div>
    </InventoryTheme>
  );
};

export default SubstitutionsReport;
